import {
  AfterViewInit,
  Component, inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {TramRegistroService} from '../../../services/database/tramRegistro.service';
import {TranslateService} from '@ngx-translate/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {CustomTitle, DataColumnType, IDataColumn, TableHeaderComponent} from 'lib-base';
import {SkeletonModule} from 'primeng/skeleton';
import {firstValueFrom, forkJoin} from 'rxjs';
import {
  IGetDashboardTramitePorDependencia,
  IGetTramiteRegistroSeleccionFechas
} from '../../../shared/interfaces/database/tramite';
import {TableModule} from 'primeng/table';
import {ProgressBarModule} from 'primeng/progressbar';
import {FilterDialogComponent} from "../../../components/custom-work/other/filter-dialog/filter-dialog.component";
import {CustomTitleDirective} from "../../../shared/clases/custom-title.directive";
import {CalendarModule} from "primeng/calendar";
import {FormsModule} from "@angular/forms";
import {MessageService} from "primeng/api";
import {FilterDashboardComponent} from "../filter-dashboard/filter-dashboard.component";
import {FilterDatesEvent} from "../../../shared/interfaces/database/configuracion";
import {ChartBarComponent, ChartBarConfig} from "../components/chart-bar/chart-bar.component";
import {DashboardTableComponent, ResumenEtapaConfig} from "../components/dashboard-table/dashboard-table.component";

@Component({
  selector: 'app-balance-general-tramite-por-dependencia',
  standalone: true,
  imports: [CommonModule, SkeletonModule, TableModule,
    ProgressBarModule, FilterDialogComponent,
    CalendarModule, FormsModule, FilterDashboardComponent, ChartBarComponent, DashboardTableComponent],
  templateUrl: './balance-general-tramite-por-dependencia.component.html',
  styleUrl: './balance-general-tramite-por-dependencia.component.scss'
})
export class BalanceGeneralTramitePorDependenciaComponent implements OnInit, AfterViewInit {
  translations: any = {};
  private readonly COLORS: am5.Color[] = [am5.color('#00CFFF'), am5.color('#002F8C'), am5.color('#0991EC'),
    am5.color('#376DD2'), am5.color('#0044A0'), am5.color('#1655B6')];
  readonly tramitesPorDependenciaChartId = "chartTramitesPorDependencia";
  isLoadingTramitesPorDependencia: boolean = true;
  tramitesPorDependencia: IGetDashboardTramitePorDependencia[] = [];
  root !: am5.Root;
  filtrarMesText: WritableSignal<string> = signal('');
  private _seleccionFechas: IGetTramiteRegistroSeleccionFechas[] = [];
  protected periodoSelect: IGetTramiteRegistroSeleccionFechas | null = null;
  size: number = 5;
  columns !: IDataColumn[];
  @ViewChild('filtrarMes') filtrarMesDialogComponent !: FilterDialogComponent<IGetTramiteRegistroSeleccionFechas>;
  @ViewChild(ChartBarComponent) barChartComponent!: ChartBarComponent;

  private titleService: CustomTitleDirective = inject(CustomTitleDirective);

  barConfig!: ChartBarConfig;
  configTable!: ResumenEtapaConfig;

  constructor(private readonly _tr: TramRegistroService, private readonly _t: TranslateService,
              public messageService: MessageService) {
    this.chartBarInit();
    this.configTablaInit();
  }


  ngOnInit() {
    this.titleService.changeTitle('components.app-balance-general-tramite-por-dependencia.titulo1');
    this.translate().then(() => {
      this.configTablaInit();
    });
  }

  ngAfterViewInit(): void {
    this.getDashboardTramitePorDependencia();
  }




  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      titulo1: this._t.get('components.app-balance-general-tramite-por-dependencia.titulo1'),
      titulo2: this._t.get('components.app-balance-general-tramite-por-dependencia.titulo2'),
      dependencia: this._t.get('common.dependencia'),
      recibidos: this._t.get('common.recibidos'),
      pendientes: this._t.get('common.pendientes'),
      finalizados: this._t.get('common.finalizados'),
      efectividad: this._t.get('common.efectividad'),
      filtrarMes: this._t.get('msg.filtrar-mes'),
      restablecer: this._t.get('common.restablecer'),
      mes: this._t.get('common.mes'),
    }));
    this.filtrarMesText.set(this.translations.filtrarMes);

  }

  //#region utils
  private castToString(date: Date): string {
    if (!date) throw 'Invalid date';
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }

  calcularPEfectividad(item: IGetDashboardTramitePorDependencia): number {
    if (item.recibidos === 0) {
      return 0;
    }
    return parseFloat(((item.finalizadosATiempo / item.recibidos) * 100).toFixed(2));

  }

  calcularPPendiente(item: IGetDashboardTramitePorDependencia): number {
    if (item.recibidos === 0) {
      return 0;
    }
    return parseFloat(((item.pendientes / item.recibidos) * 100).toFixed(2));
  }

  private agregarEfectividadAndPorcetajePendiente(array: IGetDashboardTramitePorDependencia[]) {
    return array?.map(item => {
      item.porcentajePendientes = this.calcularPPendiente(item);
      item.efectividad = this.calcularPEfectividad(item);
      return item;
    }) ?? [];

  }

  //#endregion utils

  //#region  chart
  private chartBarInit() {
    this.barConfig = {
      chartId: 'bar-grouped-tramite-por-dependencia',
      categoryField: 'dependencia',
      barHeight: 60,
      xLabelFormatter: (v: number) => `${v}`,
      tooltipValueFormatter: (v: number) => `${v}`,
      bars: [
        {
          field: 'recibidos',
          name: 'Recibidos',
          color: '#00CFFF',
          showValueLabel: true,
          valueLabelFormatter: (v: number) => `${v}`
        },
        {
          field: 'pendientes',
          name: 'Pendientes',
          color: '#2d7dff',
          showValueLabel: true,
          valueLabelFormatter: (v: number) => `${v}`
        },
        {
          field: 'finalizadosFueraPlazo',
          name: 'Finalizados fuera de plazo',
          color: '#0044A0',
          showValueLabel: true,
          valueLabelFormatter: (v: number) => `${v}`
        },
        {
          field: 'finalizadosATiempo',
          name: 'Finalizados a tiempo',
          color: '#1f9d55',
          showValueLabel: true,
          valueLabelFormatter: (v: number) => `${v}`
        },
      ],
      data: []
    }
  }

  chartBarLoad() {
    this.barChartComponent.load(this.tramitesPorDependencia)
  }

  //#endregion  chart

  private configTablaInit() {
    this.configTable = {
      title: this.translations.titulo2,
      columns: [
        {
          field: 'dependencia',
          header: this.translations.dependencia,
          type: 'dot',rowClass:'color'
        },
        {
          field: 'recibidos',
          header: this.translations.recibidos,
          type: 'text',cssClass:'text-right'
        },
        {
          field: 'pendientes',
          header: this.translations.pendientes,
          type: 'text',cssClass:'text-right'
        },
        {
          field: 'finalizadosATiempo',
          header: this.translations.finalizados,
          type: 'text',cssClass:'text-right'
        },
        {
          field: 'porcentajePendientes',
          header: '% ' + this.translations.pendientes,
          type: 'progress', rowClass: 'pendienteColor',  maxValue: 100, suffix: '%', cssClass: 'text-center'
        },
        {
          field: 'efectividad',
          header: this.translations.efectividad,
          type: 'progress', rowClass: 'efectividadColor',  maxValue: 100, suffix: '%', cssClass: 'text-center'
        },
      ],
      data:[]
    }
  }
  loadDataTable(){
    const rows = this.tramitesPorDependencia.map(item => ({
      ...item,
      efectividad: Math.round(item.efectividad ?? 0),
      porcentajePendientes: Math.round(item.porcentajePendientes ?? 0),
      pendienteColor: (item.porcentajePendientes ?? 0) > 50 ? 'gio-orange-bg' : 'gio-amber-bg',
      efectividadColor: (item.efectividad ?? 0) > 50 ? 'gio-green-bg' : 'gio-blue-bg-2',
    }))
    this.configTable = {
      ...this.configTable,
      data: rows
    }
  }

  //#region API calls
  getDashboardTramitePorDependencia(firstDate?: Date, lastDate?: Date) {
    this.isLoadingTramitesPorDependencia = true;

    const startDate = firstDate ? this.castToString(firstDate) : undefined;
    const endDate = lastDate ? this.castToString(lastDate) : undefined;

    this._tr.getDashboardTramitePorDependencia(startDate, endDate).subscribe({
      next: (response: IGetDashboardTramitePorDependencia[]) => {
        this.tramitesPorDependencia = this.agregarEfectividadAndPorcetajePendiente(response);
        this.chartBarLoad();
        this.loadDataTable();
      }, complete: () => {
        this.isLoadingTramitesPorDependencia = false;
      }
    });
  }

  //#endregion

  //#region actions
  filtrarMesOnSeleccionar(event: IGetTramiteRegistroSeleccionFechas) {
    this.periodoSelect = event;
    this.filtrarMesText.set(event ? this.translations.mes + ': ' + event.monthYear : this.translations.filtrarMes);
  }

  restablecer() {
    this.filtrarMesDialogComponent?.resetValue();
    this.filtrarMesText.set(this.translations.mes);
    this.periodoSelect = null;
    this.getDashboardTramitePorDependencia();
  }

  changeDates(event: FilterDatesEvent) {
    this.getDashboardTramitePorDependencia(event.startDate, event.endDate);
  }

  //#endregion actions
}
